import React from "react";
import { motion } from "framer-motion";

/**
 * PaperDiagrams — interactive SVG diagrams for each technical report.
 * Strict monochrome: white, zinc gray, one orange accent.
 * No bright colors. No paper-cream tints.
 *
 * Each diagram is exported individually and selected by `key` in PaperDetailPage.
 */

const W = "#FAFAFA";
const G1 = "rgba(255,255,255,0.55)";   // mid gray
const G2 = "rgba(255,255,255,0.28)";   // light hairline
const G3 = "rgba(255,255,255,0.10)";   // softest fill
const O  = "#FF6600";                  // brand orange (sparingly)

/* ============================================================
   THEMIS. ΘΕΜΙΣ pipeline (paper HB-TR-2026-02)
   Decision engine → Firewall → PII gateway → Ledger ← Verifier
   ============================================================ */
export function ThemisPipeline() {
  const node = (x, y, w, h, label, sub) => (
    <g>
      <rect x={x} y={y} width={w} height={h} rx="10" fill={G3} stroke={G2} />
      <text x={x + w / 2} y={y + h / 2 - 4} textAnchor="middle" fill={W} fontSize="11" fontFamily="Satoshi" fontWeight="500">{label}</text>
      <text x={x + w / 2} y={y + h / 2 + 12} textAnchor="middle" fill={G1} fontSize="9" fontFamily="JetBrains Mono" letterSpacing="0.1em">{sub}</text>
    </g>
  );
  const arrow = (x1, y1, x2, y2, dashed = false) => (
    <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={G1} strokeWidth="1" strokeDasharray={dashed ? "3 3" : "0"} markerEnd="url(#arr)" />
  );

  return (
    <motion.svg viewBox="0 0 720 380" className="w-full h-auto" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.8 }}>
      <defs>
        <marker id="arr" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto">
          <path d="M0,0 L6,4 L0,8" fill="none" stroke={G1} strokeWidth="1" />
        </marker>
      </defs>

      {/* Top row */}
      {node(20,  40, 120, 56, "Decision engine", "stochastic")}
      {node(180, 40, 120, 56, "Compliance firewall", "SMT")}
      {node(340, 40, 120, 56, "PII gateway", "egress")}
      {node(500, 40, 120, 56, "Executor", "action")}

      {/* Arrows top */}
      {arrow(140, 68, 180, 68)}
      {arrow(300, 68, 340, 68)}
      {arrow(460, 68, 500, 68)}

      {/* Down arrows (seals) */}
      {arrow(80,  96, 80,  180, true)}
      {arrow(240, 96, 240, 180, true)}
      {arrow(400, 96, 400, 180, true)}
      {arrow(560, 96, 560, 180, true)}

      {/* Ledger */}
      <rect x="20" y="180" width="600" height="58" rx="10" fill="rgba(255,102,0,0.05)" stroke="rgba(255,102,0,0.30)" />
      <text x="320" y="208" textAnchor="middle" fill={W} fontSize="13" fontFamily="Satoshi" fontWeight="500">Append-only hash-linked ledger · Ed25519 signed</text>
      <text x="320" y="225" textAnchor="middle" fill={O} fontSize="10" fontFamily="JetBrains Mono" letterSpacing="0.18em">r&apos; ← H(r ∥ c ∥ meta)</text>

      {/* Verifier (right) */}
      {node(500, 280, 120, 56, "External verifier", "audit")}
      {arrow(560, 280, 560, 238)}

      {/* Human review (left, dashed feedback) */}
      {node(20, 280, 140, 56, "Human review", "obligations unmet")}
      <path d="M 240 96 Q 240 270 160 308" fill="none" stroke={G2} strokeDasharray="3 3" markerEnd="url(#arr)" />
    </motion.svg>
  );
}

/* ============================================================
   ABM. Differentiable computation graph (paper HB-TR-2026-03)
   ============================================================ */
export function DifferentiableABM() {
  return (
    <motion.svg viewBox="0 0 720 320" className="w-full h-auto" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.8 }}>
      <defs>
        <marker id="arr2" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto">
          <path d="M0,0 L6,4 L0,8" fill="none" stroke={G1} strokeWidth="1" />
        </marker>
      </defs>

      {/* Forward chain */}
      <g>
        {[
          { x: 40,  label: "θ", sub: "params" },
          { x: 180, label: "π", sub: "logits" },
          { x: 320, label: "softmax((logπ+g)/τ)", sub: "Gumbel-softmax" },
          { x: 480, label: "s_t", sub: "state" },
          { x: 620, label: "L", sub: "loss" },
        ].map((n, i) => (
          <g key={i}>
            <circle cx={n.x} cy="120" r="32" fill={G3} stroke={G2} />
            <text x={n.x} y="118" textAnchor="middle" fill={W} fontSize="13" fontFamily="JetBrains Mono">{n.label}</text>
            <text x={n.x} y="170" textAnchor="middle" fill={G1} fontSize="9" fontFamily="JetBrains Mono" letterSpacing="0.1em">{n.sub}</text>
            {i < 4 && <line x1={n.x + 32} y1="120" x2={[180, 320, 480, 620][i] - 32} y2="120" stroke={G1} strokeWidth="1" markerEnd="url(#arr2)" />}
          </g>
        ))}
      </g>

      {/* Gumbel noise injection */}
      <g>
        <circle cx="320" cy="40" r="20" fill="rgba(255,102,0,0.10)" stroke="rgba(255,102,0,0.45)" />
        <text x="320" y="44" textAnchor="middle" fill={O} fontSize="11" fontFamily="JetBrains Mono">g</text>
        <text x="320" y="20" textAnchor="middle" fill={O} fontSize="9" fontFamily="JetBrains Mono" letterSpacing="0.1em">Gumbel</text>
        <line x1="320" y1="60" x2="320" y2="88" stroke={O} strokeWidth="1" strokeDasharray="2 2" markerEnd="url(#arr2)" />
      </g>

      {/* Backprop arrow (reverse) */}
      <path d="M 620 220 Q 360 270 40 220" fill="none" stroke={W} strokeWidth="1" markerEnd="url(#arr2)" strokeDasharray="0" opacity="0.7" />
      <text x="360" y="265" textAnchor="middle" fill={W} fontSize="10" fontFamily="JetBrains Mono" letterSpacing="0.15em">∂L / ∂θ · reverse-mode AD</text>
    </motion.svg>
  );
}

/* ============================================================
   NEUROSYMBOLIC. Propose-verify loop (paper HB-TR-2026-04)
   ============================================================ */
export function NeurosymbolicLoop() {
  return (
    <motion.svg viewBox="0 0 720 320" className="w-full h-auto" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.8 }}>
      <defs>
        <marker id="arr3" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto">
          <path d="M0,0 L6,4 L0,8" fill="none" stroke={G1} strokeWidth="1" />
        </marker>
      </defs>

      {/* Sources */}
      <rect x="20" y="40" width="120" height="52" rx="10" fill={G3} stroke={G2} />
      <text x="80" y="64" textAnchor="middle" fill={W} fontSize="11" fontFamily="Satoshi" fontWeight="500">Heterogeneous</text>
      <text x="80" y="80" textAnchor="middle" fill={G1} fontSize="10" fontFamily="JetBrains Mono">sources</text>

      {/* Neural proposer */}
      <rect x="200" y="40" width="160" height="52" rx="10" fill={G3} stroke="rgba(255,255,255,0.50)" />
      <text x="280" y="64" textAnchor="middle" fill={W} fontSize="12" fontFamily="Satoshi" fontWeight="500">Neural proposer</text>
      <text x="280" y="82" textAnchor="middle" fill={G1} fontSize="10" fontFamily="JetBrains Mono">LLM extractor</text>

      {/* Symbolic verifier */}
      <rect x="420" y="40" width="160" height="52" rx="10" fill="rgba(255,102,0,0.06)" stroke="rgba(255,102,0,0.45)" />
      <text x="500" y="64" textAnchor="middle" fill={W} fontSize="12" fontFamily="Satoshi" fontWeight="500">Symbolic verifier</text>
      <text x="500" y="82" textAnchor="middle" fill={O} fontSize="10" fontFamily="JetBrains Mono">SMT solver</text>

      {/* Output */}
      <rect x="620" y="40" width="80" height="52" rx="10" fill={G3} stroke={G2} />
      <text x="660" y="64" textAnchor="middle" fill={W} fontSize="11" fontFamily="Satoshi" fontWeight="500">Certified</text>
      <text x="660" y="80" textAnchor="middle" fill={G1} fontSize="10" fontFamily="JetBrains Mono">ontology</text>

      {/* Forward arrows */}
      <line x1="140" y1="66" x2="200" y2="66" stroke={G1} strokeWidth="1" markerEnd="url(#arr3)" />
      <line x1="360" y1="66" x2="420" y2="66" stroke={G1} strokeWidth="1" markerEnd="url(#arr3)" />
      <line x1="580" y1="66" x2="620" y2="66" stroke={G1} strokeWidth="1" markerEnd="url(#arr3)" />

      {/* Feedback loop (unsat core) */}
      <path d="M 500 92 Q 500 220 280 220 L 280 110" fill="none" stroke={O} strokeWidth="1.2" strokeDasharray="4 3" markerEnd="url(#arr3)" />
      <text x="380" y="245" textAnchor="middle" fill={O} fontSize="10" fontFamily="JetBrains Mono" letterSpacing="0.15em">unsat core · constrain next proposal</text>

      {/* Mantra */}
      <text x="360" y="290" textAnchor="middle" fill={G1} fontSize="11" fontFamily="editorial-new" fontStyle="italic">LLM proposes, solver disposes</text>
    </motion.svg>
  );
}

/* ============================================================
   MEMORY. Bitemporal evidence ledger + memory index (HB-TR-2026-05)
   ============================================================ */
export function BitemporalMemory() {
  return (
    <motion.svg viewBox="0 0 720 360" className="w-full h-auto" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.8 }}>
      <defs>
        <marker id="arr4" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto">
          <path d="M0,0 L6,4 L0,8" fill="none" stroke={G1} strokeWidth="1" />
        </marker>
      </defs>

      {/* Evidence ledger (top) */}
      <rect x="40" y="40" width="640" height="72" rx="10" fill="rgba(255,102,0,0.05)" stroke="rgba(255,102,0,0.30)" />
      <text x="60" y="65" fill={W} fontSize="13" fontFamily="Satoshi" fontWeight="500">Evidence ledger · append-only, hash-linked</text>
      {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((i) => (
        <g key={i}>
          <rect x={70 + i * 58} y="80" width="48" height="22" rx="3" fill="rgba(255,102,0,0.15)" stroke="rgba(255,102,0,0.4)" />
          <text x={94 + i * 58} y="95" textAnchor="middle" fill={O} fontSize="9" fontFamily="JetBrains Mono">L{i + 1}</text>
        </g>
      ))}

      {/* Memory index (bottom) */}
      <rect x="40" y="220" width="640" height="72" rx="10" fill={G3} stroke={G2} />
      <text x="60" y="245" fill={W} fontSize="13" fontFamily="Satoshi" fontWeight="500">Memory index · mutable, derived</text>
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <g key={i}>
          <circle cx={130 + i * 88} cy="272" r="14" fill={G3} stroke={G1} />
          <text x={130 + i * 88} y="276" textAnchor="middle" fill={W} fontSize="9" fontFamily="JetBrains Mono">M{i + 1}</text>
        </g>
      ))}

      {/* Projection arrows */}
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <line key={i} x1={130 + i * 88} y1="258" x2={130 + i * 88} y2="130" stroke={G2} strokeWidth="1" strokeDasharray="2 3" markerEnd="url(#arr4)" />
      ))}

      <text x="360" y="180" textAnchor="middle" fill={G1} fontSize="11" fontFamily="JetBrains Mono" letterSpacing="0.15em">M ← Π(L)</text>

      {/* As-known-at probe */}
      <g>
        <text x="60" y="332" fill={W} fontSize="11" fontFamily="editorial-new" fontStyle="italic">As-known-at query</text>
        <line x1="60" y1="338" x2="660" y2="338" stroke={W} strokeWidth="0.5" />
        {[110, 230, 350, 470, 590].map((x, i) => (
          <line key={i} x1={x} y1="334" x2={x} y2="342" stroke={W} strokeWidth="0.5" />
        ))}
        <text x="660" y="332" textAnchor="end" fill={G1} fontSize="10" fontFamily="JetBrains Mono">τ_x</text>
      </g>
    </motion.svg>
  );
}

/* Registry — selected by name in PaperDetailPage */
export const DIAGRAMS = {
  themisPipeline:    ThemisPipeline,
  differentiableABM: DifferentiableABM,
  neurosymbolic:     NeurosymbolicLoop,
  bitemporalMemory:  BitemporalMemory,
};
